import type { DsplProcessorMessagePayload } from './dspl-processor'

import { Deferred } from 'everyday-utils'
import { Build, Frontend, GenRuntime, createMemory } from './frontend'
import { VLiteral } from './producer'
import { say } from './sam'
import { post } from './util'
import { fetchBinary, initVm } from './vm'

export type DsplNodeMessagePayload =
  | { onError: Error }
  | { onReady: true }
  | { onBuild: true }
  | { onPreview: true }

export interface DsplNodeOptions extends AudioWorkletNodeOptions {
  memory: WebAssembly.Memory
  controlRate: number
}

function sinc(x: number): number {
  if (x === 0) {
    return 1;
  }
  const piX = Math.PI * x;
  return Math.sin(piX) / piX;
}

function lanczosKernel(x: number, a: number): number {
  if (x === 0) {
    return 1;
  }
  if (Math.abs(x) >= a) {
    return 0;
  }
  return sinc(x) * sinc(x / a);
}

function upsampleBuffer(buffer: Float32Array, a: number): Float32Array {
  const originalLength = buffer.length
  const targetLength = 65536;
  const upsampledBuffer = new Float32Array(targetLength);
  const ratio = originalLength / targetLength

  for (let i = 0; i < targetLength; i++) {
    const position = i * ratio;
    let value = 0;

    for (let j = -a + 1; j < a; j++) {
      const index = Math.round(position + j);
      if (index >= 0 && index < originalLength) {
        value += (buffer[index] ?? 0) * lanczosKernel(j, a);
      }
    }

    upsampledBuffer[i] = value;
  }
  // console.log('UPSAMPLED', upsampleBuffer)
  return upsampledBuffer;
}

export class DsplNode extends AudioWorkletNode {
  static registered: Set<BaseAudioContext> = new Set()
  static vmBinary: Uint8Array

  static async register(context: BaseAudioContext) {
    if (this.registered.has(context)) return

    const [vmBinary] = await Promise.all([
      fetchBinary(false),
      context.audioWorklet.addModule(
        // @ts-ignore
        new URL('./dspl-processor.js', import.meta.url).href
      )
    ])
    this.vmBinary = vmBinary

    this.registered.add(context)
  }

  static async create(
    context: BaseAudioContext,
    options: DsplNodeOptions = {
      memory: createMemory(),
      controlRate: 64,
    }
  ) {
    await this.register(context)
    const node = new this(context, options)
    await node.init()
    return node
  }

  frontend!: Frontend

  get running() {
    return this.frontend.running
  }

  samples: Map<number, Deferred<Float32Array[]>> = new Map()
  assignedSamples: Map<GenRuntime, number> = new Map()
  speeches: Map<string, Deferred<Float32Array>> = new Map()
  assignedSpeeches: Map<GenRuntime, string> = new Map()

  declare fetchSample: (sampleId: number) => Promise<Float32Array[]>

  declare onError: (error: Error) => void
  declare onReady: () => void
  declare onBuild: () => void
  declare onPreview: () => void

  constructor(context: BaseAudioContext, public options: DsplNodeOptions) {
    super(context, 'dspl', {
      ...options,
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2
    })
    this.port.onmessage = (e: MessageEvent<DsplNodeMessagePayload>) => {
      if ('onError' in e.data) {
        this.onError?.(e.data.onError)
      }
      if ('onReady' in e.data) {
        this.onReady?.()
      }
      if ('onBuild' in e.data) {
        // console.log('received onBuild')
        this.onBuild?.()
      }
      if ('onPreview' in e.data) {
        this.onPreview?.()
      }
    }
  }

  async init() {
    const vm = await initVm(DsplNode.vmBinary, this.options.memory)
    this.frontend = new Frontend(vm)
    this.frontend.debug = true
    const deferred = Deferred()
    this.onReady = deferred.resolve as () => void
    post<DsplProcessorMessagePayload>(this.port, {
      initVm: [DsplNode.vmBinary, this.options.memory, this.frontend.buffers]
    })
    await deferred.promise
  }

  fetchSamples = async () => {
    const build = this.frontend.active
    if (build) {
      for (const g of build.info.scope.gens.values()) {
        const gen = g.runtime!

        const text = g.locals.gen_say_set_text
        if (text) {
          if (!this.speeches.has(text)) {
            const deferred: Deferred<Float32Array> = Deferred()
            this.speeches.set(text, deferred)
            const floats = await say(text)
            deferred.resolve(upsampleBuffer(floats, 3))
          }
          if (this.assignedSpeeches.get(gen) !== text) {
            const deferred = this.speeches.get(text)!
            const floats = await deferred.promise
            if (floats) {
              gen.floats!.set(floats)
            }
            this.assignedSpeeches.set(gen, text)
          }
          continue
        }

        const id = g.locals.gen_freesound_set_id
        if (!id) continue

        const literal = g.tokens.find((t) => (t as VLiteral).code === id)
        const sampleId = parseInt(literal?.text ?? '0')
        if (!sampleId) continue

        try {
          if (sampleId) {
            // console.log('GOT SAMPLE ID', sampleId)
            if (!this.samples.has(sampleId)) {
              const deferred: Deferred<Float32Array[]> = Deferred()
              this.samples.set(sampleId, deferred)
              const floatsStereo = await this.fetchSample(sampleId)
              deferred.resolve(floatsStereo.map((floats) => upsampleBuffer(floats, 3)))
            }
            if (this.assignedSamples.get(gen) !== sampleId) {
              const deferred = this.samples.get(sampleId)!
              const floatsStereo = await deferred.promise
              if (floatsStereo) {
                gen.floats!.set(floatsStereo[0]) // TODO: only Left channel for now, need to handle stereo
              }
              this.assignedSamples.set(gen, sampleId)
            }
          }
        } catch { }
      }
    }
  }

  run(build: Build) {
    post<DsplProcessorMessagePayload>(this.port, {
      setCtrl: [
        build.binary,
        build.payload.ins,
        build.payload.outs,
        build.payload.mems,
        build.payload.memsAfter,
        build.payload.memsReset,
      ]
    })
  }

  generatePreview(build: Build) {
    // const deferred = Deferred()
    // this.onPreview = deferred.resolve as () => void
    post<DsplProcessorMessagePayload>(this.port, {
      generatePreview: [build.payload.memsReset]
    })
    // await deferred.promise
  }

  start() {
    if (this.running) return
    this.frontend.running = true
    post<DsplProcessorMessagePayload>(this.port, {
      start: []
    })
  }

  stop() {
    if (!this.running) return
    this.frontend.running = false
    post<DsplProcessorMessagePayload>(this.port, {
      stop: []
    })
  }

  reset() {
    const mems = this.frontend.reset()
    if (mems) {
      post<DsplProcessorMessagePayload>(this.port, {
        reset: [mems]
      })
    }
  }
}
